import * as React from 'react';
import { StyleSheet } from 'react-native';
import { translate } from 'secullum-i18n';
import {
  Card,
  RangeDatePicker,
  ErrorMessage,
  DropDown,
  Space,
  Message
} from 'secullum-react-native-ui';
import InconsistenciaHeader from '../../../shared/components/InconsistenciaHeader';
import InconsistenciaLinha from '../../../shared/components/InconsistenciaLinha';
import {
  getItensStatusInconsistencias,
  InconsistenciasContainer
} from '../../../shared/containers/InconsistenciasContainer';
import withContainer from '../../../shared/containers/withContainer';
import Api from '../../../shared/modules/api';
import { retornarRotaWeb, Telas } from '../../../shared/modules/telas';
import { buscarPrimeiraTelaInicialDisponivel } from '../../../shared/modules/telasIniciais';
import {
  InconsistenciasFiltros,
  InconsistenciasFiltrosStatus,
  InconsistenciasDados,
  TipoPlano
} from '../../../shared/modules/types';
import { getDataInicioDiasAtras } from '../../../shared/modules/utils';
import LoggedInScreen from '../../components/LoggedInScreen';
import { navegar, RouteComponentComBancoIdProps } from '../../modules/routes';
import InconsistenciasDetalhes from './InconsistenciasDetalhes';

interface Props extends RouteComponentComBancoIdProps {
  container: InconsistenciasContainer;
}

interface State {
  filtros: InconsistenciasFiltros;
  erro: string;
  inconsistencia: InconsistenciasDados | null;
}

class Inconsistencias extends React.Component<Props, State> {
  state: State = {
    filtros: {
      dataInicio: getDataInicioDiasAtras(14),
      dataFinal: new Date(),
      status: InconsistenciasFiltrosStatus.Pendentes
    },
    erro: '',
    inconsistencia: null
  };

  async componentDidMount() {
    await this.props.container.loadAsync(this.state.filtros);
  }

  handleDataInicioChange = (value: Date) => {
    this.setState({
      ...this.state,
      filtros: { ...this.state.filtros, dataInicio: value, dataFinal: value },
      erro: ''
    });
  };

  handleChange = (value: string | Date, name: keyof State['filtros']) => {
    const novosFiltros = {
      ...this.state.filtros,
      [name]: value
    };
    this.setState(
      {
        ...this.state,
        filtros: novosFiltros,
        erro: ''
      },
      async () => {
        if (
          this.state.filtros.dataInicio &&
          this.state.filtros.dataFinal &&
          this.state.filtros.dataInicio > this.state.filtros.dataFinal
        ) {
          this.setState({
            ...this.state,
            erro: translate('Selecione o período corretamente')
          });

          return;
        }

        await this.props.container.loadAsync(this.state.filtros);
      }
    );
  };

  render() {
    const { container } = this.props;
    const { filtros, erro, inconsistencia } = this.state;

    const api = new Api();
    const { dados } = api.funcionario.state;

    // Sec-issues#13175: Caso o usuário preencha a rota de inconsistencias manualmente,
    // caso não tenha permissão para acesso, direcionamos para a proxima tela disponível
    if (
      dados.plano < TipoPlano.Ultimate ||
      !dados.usaExigirJustificativaInconsistencias
    ) {
      const telaRedirecionar = buscarPrimeiraTelaInicialDisponivel(
        dados,
        Telas.Inconsistencias
      );

      navegar({
        props: this.props,
        path: retornarRotaWeb(telaRedirecionar)
      });
    }

    return (
      <LoggedInScreen screenTitle={translate('Inconsistências')}>
        <Card>
          <Card.Header title={translate('Filtrar')} />
          <Card.Section style={styles.section}>
            <DropDown
              nativeID="inconsistencias-status"
              label={translate('Status da Inconsistência')}
              items={getItensStatusInconsistencias()}
              value={filtros.status}
              onChange={status => this.handleChange(status, 'status')}
              style={styles.status}
              inputStyle={styles.inputStatus}
              emptyMessage={translate('Sua busca não retornou resultados')}
            />
            <RangeDatePicker
              nativeID="inconsistencias-data"
              style={styles.datePicker}
              label={translate('Insira o Período')}
              startDate={filtros.dataInicio!}
              endDate={filtros.dataFinal!}
              onStartDateChange={date => this.handleDataInicioChange(date)}
              onEndDateChange={date => this.handleChange(date, 'dataFinal')}
            />
            <ErrorMessage nativeID="inconsistencias-erro" message={erro} />
          </Card.Section>
        </Card>
        <Space height={10} />
        <Card>
          <Card.Section style={styles.headerSection}>
            <InconsistenciaHeader />
          </Card.Section>
        </Card>
        <Space height={10} />
        {container.state.lista.map((inconsistencia, index) => (
          <InconsistenciaLinha
            inconsistenciaNativeId={`inconsistencia-${index + 1}`}
            key={inconsistencia.id}
            exibirSeparador={index > 0}
            inconsistencia={inconsistencia}
            onNavigate={() => {
              this.setState({ inconsistencia });
            }}
          />
        ))}
        {inconsistencia && (
          <InconsistenciasDetalhes
            modalVisible={inconsistencia !== null}
            inconsistencia={inconsistencia}
            onCancelar={() => this.setState({ inconsistencia: null })}
            onConcluir={() =>
              this.setState(
                { inconsistencia: null },
                async () => await container.loadAsync(this.state.filtros)
              )
            }
          />
        )}
        <Message
          type="warning"
          visible={container.state.erro !== ''}
          message={container.state.erro}
          onRequestClose={() => container.limparErro()}
        />
      </LoggedInScreen>
    );
  }
}

const styles = StyleSheet.create({
  headerSection: {
    padding: 0
  },
  status: {
    flex: 1,
    minWidth: 250,
    maxWidth: 350,
    marginTop: 10,
    marginLeft: 16
  },
  inputStatus: {
    paddingTop: 4
  },
  datePicker: {
    flex: 1,
    minWidth: 250,
    maxWidth: 350,
    marginTop: 10,
    marginLeft: 16
  },
  section: {
    flexDirection: 'row',
    flex: 8,
    flexWrap: 'wrap',
    minHeight: 88,
    paddingTop: 6,
    paddingLeft: 0
  }
});

export default withContainer(InconsistenciasContainer)(Inconsistencias);
